import { forwardRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { standingsLayout } from '../../utils/category-standings';
import { properName } from '../../utils/proper-name';
import type { StandingsCategory } from '../../services/category-standings';
import type { Competition } from '../../types/competition';
import type { ClassResults } from './useCompetitionResults';
import { SPONSORS_ATTR, SPONSORS_HEIGHT, SPONSORS_SPACE_ATTR } from './results-print-sponsors';

/**
 * A stage's results on paper: the stage — its circuit's logo, its name, its
 * day and where it was held (the venue's name, never its address) — and then
 * each class's table, place, name and squad. Nothing else: no turns, no
 * blocks, no avatars, none of the app around it — only the stage's sponsors,
 * at the foot of every page (`results-print-sponsors`). Names are printed in
 * one case (`properName`), however the desk typed them.
 *
 * Styled inline, every bit of it: it is printed through `printNode`, which
 * takes the element's own HTML into an iframe with none of the app's
 * stylesheets. The paper's margins are the iframe's `@page`, not this.
 *
 * The browser breaks the pages. A class is kept on one page when it fits, a
 * longer one repeats its table header on the next, and `classPerPage` starts
 * every class on a page of its own — the sheets the desk pins up one by one.
 */

const C = {
  accent: '#93C92B',
  accentDark: '#4D7C0F',
  ink: '#111827',
  body: '#374151',
  muted: '#6B7280',
  line: '#E5E7EB',
  head: '#F3F4F6',
};

/** The first three places, as a medal's colour behind the number. */
const PODIUM: Record<number, string> = { 1: '#F6D365', 2: '#D7DCE3', 3: '#E5B48A' };

const s = {
  sheet: {
    fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    color: C.body,
    background: '#ffffff',
    fontSize: 11,
    lineHeight: 1.4,
    width: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    paddingBottom: 14,
    borderBottom: `3px solid ${C.accent}`,
  },
  logo: { width: 64, height: 64, objectFit: 'contain', flexShrink: 0 },
  eyebrow: {
    fontSize: 10,
    fontWeight: 700,
    color: C.accentDark,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    margin: 0,
  },
  title: { fontSize: 24, fontWeight: 700, color: C.ink, margin: '2px 0 4px' },
  meta: { fontSize: 12, color: C.muted, margin: 0 },
  heading: {
    fontSize: 13,
    fontWeight: 700,
    color: C.muted,
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    margin: '18px 0 0',
  },
  category: { breakInside: 'avoid', pageBreakInside: 'avoid', marginTop: 20 },
  categoryName: {
    fontSize: 16,
    fontWeight: 700,
    color: C.ink,
    margin: '0 0 8px',
    paddingLeft: 8,
    borderLeft: `4px solid ${C.accent}`,
    breakAfter: 'avoid',
    pageBreakAfter: 'avoid',
  },
  sectionName: { fontSize: 12, fontWeight: 600, color: C.body, margin: '10px 0 6px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 11 },
  th: {
    textAlign: 'left',
    fontWeight: 600,
    fontSize: 9,
    color: C.muted,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    padding: '5px 8px',
    background: C.head,
    borderBottom: `1px solid ${C.line}`,
  },
  td: { padding: '5px 8px', borderBottom: `1px solid ${C.line}` },
  row: { breakInside: 'avoid', pageBreakInside: 'avoid' },
  place: {
    display: 'inline-block',
    minWidth: 22,
    padding: '1px 4px',
    borderRadius: 11,
    textAlign: 'center',
    fontWeight: 700,
    color: C.ink,
    fontVariantNumeric: 'tabular-nums',
  },
  name: { fontWeight: 600, color: C.ink },
  team: { color: C.muted },
  note: { fontSize: 11, fontStyle: 'italic', color: C.muted, margin: '4px 0 0' },
  // The page around the content: a table so that its foot — the room kept for
  // the sponsors — is repeated at the bottom of every printed page.
  frame: { width: '100%', borderCollapse: 'collapse' },
  frameCell: { padding: 0, verticalAlign: 'top' },
  sponsors: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '2mm 8mm',
    height: SPONSORS_HEIGHT,
    marginTop: 20,
    paddingTop: '3mm',
    borderTop: `1px solid ${C.line}`,
    background: '#ffffff',
    overflow: 'hidden',
  },
  sponsorLogo: { height: '9mm', maxWidth: '36mm', objectFit: 'contain' },
} satisfies Record<string, CSSProperties>;

interface ResultsPrintSheetProps {
  competition: Competition;
  categories: StandingsCategory[];
  resultsOf: (id: string) => ClassResults;
  /** Start every class on a page of its own. */
  classPerPage?: boolean;
}

/** The stage's day, or its first and last when it ran over more than one. */
function stageDates(competition: Competition, locale: string): string | null {
  const format = (date: string) =>
    new Date(date).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
  const { start_date: start, end_date: end } = competition;
  if (start && end && start !== end) return `${format(start)} — ${format(end)}`;
  const day = start ?? end;
  return day ? format(day) : null;
}

export const ResultsPrintSheet = forwardRef<HTMLDivElement, ResultsPrintSheetProps>(function ResultsPrintSheet(
  { competition, categories, resultsOf, classPerPage = false },
  ref
) {
  const { t, i18n } = useTranslation();
  // A logo that does not load is left off rather than printed as a broken image.
  const [logoFailed, setLogoFailed] = useState(false);
  const logo = competition.trophy?.logo_small_url || competition.logo_small_url || competition.logo_url;
  const where = [competition.venue?.name, competition.city].filter(Boolean).join(', ');
  const meta = [stageDates(competition, i18n.language), where].filter(Boolean).join(' · ');

  // The circuit's sponsors then the stage's own, as under the page's cover.
  const sponsors = (competition.sponsors ?? []).filter((sponsor) => sponsor.logo_small_url || sponsor.logo_url);

  const content = (
    <>
      <header style={s.header}>
        {logo && !logoFailed && <img src={logo} alt="" style={s.logo} onError={() => setLogoFailed(true)} />}
        <div>
          {competition.trophy && <p style={s.eyebrow}>{competition.trophy.name}</p>}
          <h1 style={s.title}>{competition.name}</h1>
          {meta && <p style={s.meta}>{meta}</p>}
        </div>
      </header>

      <p style={s.heading}>{t('competitionStandings.results')}</p>

      {categories.map((category, index) => {
        const current = resultsOf(category.id);
        const sections =
          current.state === 'ready'
            ? standingsLayout(current.standings)
                .sections.map((section) => ({ section, rows: section.rows.filter((row) => row.rank != null) }))
                .filter(({ rows }) => rows.length > 0)
            : [];

        return (
          <section
            key={category.id}
            style={{ ...s.category, ...(classPerPage && index > 0 ? { breakBefore: 'page', pageBreakBefore: 'always' } : {}) }}
            data-testid={`results-print-${category.id}`}
          >
            <h2 style={s.categoryName}>{category.name}</h2>

            {current.state === 'not_closed' && <p style={s.note}>{t('competitionStandings.notClosed')}</p>}
            {current.state === 'failed' && <p style={s.note}>{t('errors.loadFailed')}</p>}
            {current.state === 'ready' && sections.length === 0 && (
              <p style={s.note}>{t('competitionStandings.empty')}</p>
            )}

            {sections.map(({ section, rows }) => (
              <div key={section.key}>
                {sections.length > 1 && (
                  <h3 style={s.sectionName}>{section.rounds.map((round) => round.name).join(' + ')}</h3>
                )}
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={{ ...s.th, width: 48 }}>{t('competitionRounds.standings.position')}</th>
                      <th style={s.th}>{t('competitionRounds.standings.athlete')}</th>
                      <th style={s.th}>{t('competitionRounds.standings.team')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ entry, rank }) => (
                      <tr key={entry.id} style={s.row}>
                        <td style={s.td}>
                          <span style={{ ...s.place, background: PODIUM[rank as number] ?? 'transparent' }}>{rank}</span>
                        </td>
                        <td style={{ ...s.td, ...s.name }}>{entry.full_name ? properName(entry.full_name) : '—'}</td>
                        <td style={{ ...s.td, ...s.team }}>{entry.team_name ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </section>
        );
      })}
    </>
  );

  if (sponsors.length === 0) {
    return (
      <div ref={ref} style={s.sheet} data-testid="results-print-sheet">
        {content}
      </div>
    );
  }

  return (
    <div ref={ref} style={s.sheet} data-testid="results-print-sheet">
      <table style={s.frame}>
        <tbody>
          <tr>
            <td style={s.frameCell}>{content}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td style={s.frameCell}>
              <div {...{ [SPONSORS_SPACE_ATTR]: '' }} />
            </td>
          </tr>
        </tfoot>
      </table>
      <footer {...{ [SPONSORS_ATTR]: '' }} style={s.sponsors} data-testid="results-print-sponsors">
        {sponsors.map((sponsor) => (
          <img
            key={sponsor.id}
            src={sponsor.logo_small_url ?? sponsor.logo_url ?? undefined}
            alt={sponsor.name}
            style={s.sponsorLogo}
          />
        ))}
      </footer>
    </div>
  );
});

export default ResultsPrintSheet;
